
import NoteMap from "@/domain/model/schedule/question/option/note/NoteMap.js";
import RcDomainObject from "../../../common/RcDomainObject.js";
import Company from "../../company/Company.js";
import CheckListItemOptionMap from "./option/CheckListItemOptionMap.js";
import CheckListItemOption from "./option/CheckListItemOption.js";
import CheckListItemMap from "./CheckListItemMap.js";
import StringUtils from '@/utils/StringUtils.js'
import VehicleData from "../../vehicle/data/VehicleData.js";

export default class CheckListItem extends RcDomainObject {
  
  static MODEL_NAME = 'CheckListItem';
  
  static Fields = {
      Index: "index",
      Name: "name",
      Type: "type",
      Company: "Company",
      CREATED_DATE: "createdDate",
      OptionMap : CheckListItemOptionMap.MODEL_NAME,
      MultiSelect: "multiSelect",
      WarnOnChange: "warnOnChange",
      WarnChangeThreshold: "warnChangeThreshold",
      Content: "content",
      Section: "section",
      VehicleTypes: "vehicleTypes",
      ShowParts: "showParts",
      // Notes on the question: the schedule's own field, a map of
      // VehicleScheduleQuestionNote records {position, value}.
      NoteMap: NoteMap.MODEL_NAME,
      // A Rollup: an ordinary "list" question whose row answers are Good, Report and
      // (when allowed) N/A, with questions of its own asked when the driver picks Report.
      // true on a Rollup; missing on everything else - it is never stored as false.
      Rollup: "rollup",
      // The Rollup's nested questions ("Questions asked on Report"), spelled out in full,
      // each with its own options spelled out. They are never in a checklist's own
      // CheckListItemMap, nor in the flat stores.
      RollupItemMap: "RollupCheckListItemMap",
      // How a Yes / No or Dropdown List question (ordinary or nested in a Rollup) shows its answers:
      // "buttons", "dropdown" or "checklist". Missing, null or "" mean "buttons" on a Rollup's
      // nested question and "checklist" on an ordinary one (see displayAs).
      DisplayAs: "displayAs",
  }

  static DisplayAs = {
      Buttons: "buttons",
      Dropdown: "dropdown",
      Checklist: "checklist",
  }
  
  static Values = {
      Types: {
        Text: "text",
        Number: "number",
        List: "list",
        Bool: "bool",
        Info: "info",
        // An hour meter on the asset the checklist is filled in against. A number
        // question in every respect, always metered, and only one to a checklist -
        // a machine has one hour meter, and its reading is kept on the asset
        // itself rather than against the question that asked for it.
        Hours: "hours"
      }
  }
  
  constructor(domain, data) {
    super(domain, data, CheckListItem.MODEL_NAME);
  }

  fields() {
    return CheckListItem.Fields;
  }
  
  find() {
    var result = this.domain.checklists().items().findById(this.id());
    if (result.id() !== this.id()) {
      result = this;
    }
    
    return result;
  }

  populate() {
    var options = this.options();
    options.populate();
    this.withOptions(options);
    return this;
  }
  
  withOptions(options) {
    this.put(CheckListItem.Fields.OptionMap, options);
    return this;
  }
  
  options() {
    var data = this.get(CheckListItem.Fields.OptionMap);
    return new CheckListItemOptionMap(this.domain, data);
  }
  
  index() {
    return this.getInt(CheckListItem.Fields.Index);
  }
  
  withIndex(index) {
    this.put(CheckListItem.Fields.Index, index);
    return this;
  }
  
  name() {
    return this.getString(CheckListItem.Fields.Name);
  }
  
  withName(name) {
    this.put(CheckListItem.Fields.Name, name);
    return this;
  }

  type() {
    return this.getString(CheckListItem.Fields.Type);
  }
  
  withType(type) {
    this.put(CheckListItem.Fields.Type, type);
    return this;
  }
  
  withTypeString() {
    return this.withType(CheckListItem.Values.Types.Text);
  }
  
  withTypeInteger() {
    return this.withType(CheckListItem.Values.Types.Number);
  }
  
  withTypeList() {
    return this.withType(CheckListItem.Values.Types.List);
  }

  withTypeBool() {
    return this.withType(CheckListItem.Values.Types.Bool);
  }

  withTypeInfo() {
    return this.withType(CheckListItem.Values.Types.Info);
  }

  withTypeHours() {
    return this.withType(CheckListItem.Values.Types.Hours);
  }

  isTypeText() {
    return StringUtils.equals(CheckListItem.Values.Types.Text, this.type());
  }
  
  // Hours counts as a number too, the way bool counts as a list: everything that
  // handles a typed-in number treats the two the same, and only the places that
  // care which meter it is ask isTypeHours().
  isTypeNumber() {
    return StringUtils.equals(CheckListItem.Values.Types.Number, this.type())
        || this.isTypeHours();
  }

  // An hour meter on the asset. Only one to a checklist.
  isTypeHours() {
    return StringUtils.equals(CheckListItem.Values.Types.Hours, this.type());
  }

  // What the answer to this question is recorded against on the asset. Every
  // metered question keeps its readings under its own id, so reworking a
  // checklist never mixes two questions' history - except the hour meter, which
  // belongs to the machine rather than to whichever question asked for it.
  readingKey() {
    return this.isTypeHours() ? VehicleData.Values.Keys.Hours : this.id();
  }
  
  isTypeBool() {
    return StringUtils.equals(CheckListItem.Values.Types.Bool, this.type());
  }
  
  isTypeList() {
    return StringUtils.equals(CheckListItem.Values.Types.List, this.type())
        || this.isTypeBool();
  }
  
  // An information block: markdown shown to whoever is filling in the checklist,
  // with nothing to answer and no result recorded against it.
  isTypeInfo() {
    return StringUtils.equals(CheckListItem.Values.Types.Info, this.type());
  }
  
  isAnswerable() {
    return !this.isTypeInfo();
  }
  
  company() {
    var data = this.get(CheckListItem.Fields.Company);
    var company = new Company(this.domain, data);
    return company;
  }
  
  withCompany(company) {
    this.put(CheckListItem.Fields.Company, company);
    return this;
  }
  
  multiSelect() {
    return this.getBoolean(CheckListItem.Fields.MultiSelect)
  }
  
  withMultiSelect(multiSelect) {
    this.put(CheckListItem.Fields.MultiSelect, multiSelect);
    return this;
  }
  
  // An hour meter is always metered - that is what the type is for - so the
  // switch a plain Number question carries is not asked about or stored for one.
  warnOnChange() {
    return this.isTypeHours() || this.getBoolean(CheckListItem.Fields.WarnOnChange);
  }
  
  withWarnOnChange(warnOnChange) {
    this.put(CheckListItem.Fields.WarnOnChange, warnOnChange);
    return this;
  }
  
  warnChangeThreshold() {
    var value = parseFloat(this.get(CheckListItem.Fields.WarnChangeThreshold));
    if (isNaN(value)) {
      return 0;
    }
    return value;
  }
  
  withWarnChangeThreshold(threshold) {
    this.put(CheckListItem.Fields.WarnChangeThreshold, threshold);
    return this;
  }
  
  hasChangeWarning() {
    return this.isTypeNumber()
        && this.warnOnChange()
        && this.warnChangeThreshold() > 0;
  }
  
  content() {
    return this.getString(CheckListItem.Fields.Content);
  }
  
  withContent(content) {
    this.put(CheckListItem.Fields.Content, content);
    return this;
  }
  
  hasContent() {
    return !StringUtils.isEmpty(this.content());
  }
  
  // A one-line plain-text stand-in for an information block, for the places that
  // list items by name and have no room to render the markdown itself.
  contentSummary() {
    var lines = this.content().split("\n");
    for (var index = 0; index < lines.length; index++) {
      var line = lines[index]
        .replace(/^\s*[#>\-*+]+\s*/, "")
        .replace(/[*_`]/g, "")
        .trim();
      if (line.length > 0) {
        return line.length > 80 ? line.substring(0, 80) + "..." : line;
      }
    }
    return "";
  }
  
  // The heading the item is grouped under, such as "Brakes". Empty for none.
  section() {
    return this.getString(CheckListItem.Fields.Section);
  }
  
  withSection(section) {
    this.put(CheckListItem.Fields.Section, section);
    return this;
  }
  
  hasSection() {
    return !StringUtils.isEmpty(this.section());
  }
  
  // The vehicle types the item is asked for, comma-separated, in the schedule
  // question vocabulary (vehicle, trailer, abs, hbs, ebs, nbs). Empty means all.
  vehicleTypes() {
    return this.getString(CheckListItem.Fields.VehicleTypes);
  }
  
  withVehicleTypes(vehicleTypes) {
    this.put(CheckListItem.Fields.VehicleTypes, vehicleTypes);
    return this;
  }
  
  // The vehicle types as a list, trimmed and lower case, with blanks left out.
  vehicleTypeList() {
    return this.vehicleTypes()
      .split(",")
      .map(type => type.trim().toLowerCase())
      .filter(type => type.length > 0);
  }
  
  // Whether the item is asked for the given vehicle type. An item that names no
  // types is asked for every one. Types are matched whole, without regard to case.
  appliesTo(vehicleType) {
    var types = this.vehicleTypeList();
    if (types.length === 0) {
      return true;
    }
    var wanted = StringUtils.toString(vehicleType).trim().toLowerCase();
    return types.includes(wanted);
  }

  // Whether the item is asked on this vehicle, by the schedule rule (the server's
  // Question.forVehicle): a trailer needs "trailer" and its braking type in the list;
  // anything else needs only its braking type - the "vehicle" flag itself is not
  // checked. An item that names no types is asked on every vehicle (a schedule question
  // always names some, so this is the one place the rules differ).
  //
  // The tests are substring tests on the stored string, as Question.hasVehicleType's
  // are, so the answers are the same: a vehicle with no braking type passes the brake
  // test against any non-empty list, and "trailerabs" counts as naming both. Unlike the
  // server, case is ignored. Nothing in the inspection flow calls this yet.
  appliesToVehicle(vehicle) {
    if (this.vehicleTypeList().length === 0) {
      return true;
    }
    var types = this.vehicleTypes().toLowerCase();
    var brakingType = StringUtils.toString(vehicle.brakeType()).trim().toLowerCase();
    var brakesMatch = types.includes(brakingType);
    if (vehicle.isTrailer()) {
      return types.includes("trailer") && brakesMatch;
    }
    return brakesMatch;
  }

  // Whether the answers' Part letters are shown, the "a) " in "a) Front lamps" on a
  // schedule. Off unless set: a question saved before the switch existed has no key.
  showParts() {
    var value = this.get(CheckListItem.Fields.ShowParts);
    return value === true || value === "true";
  }

  // Switching the letters on writes true. Switching them off writes false only to a
  // question that already holds a value, so a question nobody touched does not gain
  // one. A read returns the property as null when it was never set, which counts as
  // not held.
  withShowParts(showParts) {
    if (showParts === true) {
      this.put(CheckListItem.Fields.ShowParts, true);
    } else if (this.get(CheckListItem.Fields.ShowParts) != null) {
      this.put(CheckListItem.Fields.ShowParts, false);
    }
    return this;
  }

  // Notes on the question itself: guidance shown under it, as a schedule prints under a
  // heading. Each note's value is Markdown, and they show in position order as "Note 1",
  // "Note 2". An empty map when the question has none, including when a read returns the
  // property as null because it was never set.
  noteMap() {
    return new NoteMap(this.domain, this.get(CheckListItem.Fields.NoteMap));
  }

  withNoteMap(noteMap) {
    this.put(CheckListItem.Fields.NoteMap, noteMap);
    return this;
  }

  // The notes in position order.
  noteList() {
    return this.noteMap().sortByPosition();
  }

  // Whether any note says anything; blank notes count as none.
  hasNotes() {
    return this.noteMap().hasValues();
  }

  displayLabel() {
    if (this.isTypeInfo()) {
      var summary = this.contentSummary();
      return summary.length > 0 ? summary : "Information";
    }
    return this.name();
  }
  // How a Yes / No or Dropdown List question shows its answers. When nothing is stored
  // (missing, null or ""), a Rollup's nested question shows buttons and an ordinary
  // question a checklist, so the caller says which it is asking about.
  displayAs({ nested = false } = {}) {
    const value = this.getString(CheckListItem.Fields.DisplayAs).toLowerCase();
    if (value) {
      return value;
    }
    return nested ? CheckListItem.DisplayAs.Buttons : CheckListItem.DisplayAs.Checklist;
  }

  // Whether a Display is saved at all, as opposed to falling back to the default above.
  hasDisplayAs() {
    return !!this.getString(CheckListItem.Fields.DisplayAs);
  }

  // Buttons is stored as "buttons", never by removing the key: the server keeps any
  // property a save leaves out, so a missing key would leave the old value in place.
  withDisplayAs(displayAs) {
    this.put(CheckListItem.Fields.DisplayAs, displayAs ? displayAs : CheckListItem.DisplayAs.Buttons);
    return this;
  }

  // Whether this is a Rollup. Its stored type stays "list", so isTypeList() holds for it
  // too; anything that treats a Rollup differently asks this first.
  isRollup() {
    return this.getBoolean(CheckListItem.Fields.Rollup) === true;
  }

  // Turning it on stores true. Turning it off removes the flag and the nested questions,
  // since the flag is never stored as false and only a Rollup carries nested questions.
  withRollup(rollup) {
    if (rollup === true) {
      this.put(CheckListItem.Fields.Rollup, true);
    } else {
      delete this.data[CheckListItem.Fields.Rollup];
      delete this.data[CheckListItem.Fields.RollupItemMap];
    }
    return this;
  }

  // The nested questions as held on the record. Never null: an item with none, or a
  // read that returned the property as null, gives an empty map. The questions are
  // read from the record itself and never looked up in the stores, which do not hold
  // them - so callers must not find() or populate() this map.
  rollupItems() {
    var data = this.get(CheckListItem.Fields.RollupItemMap);
    if (Array.isArray(data)) {
      var keyed = {};
      data.forEach(entry => {
        if (entry != null && typeof entry === 'object' && entry['@rid']) {
          keyed[entry['@rid']] = entry;
        } else if (typeof entry === 'string') {
          keyed[entry] = { '@rid': entry };
        }
      });
      data = keyed;
    }
    if (data == null || typeof data !== 'object') {
      data = {};
    }
    return new CheckListItemMap(this.domain, data);
  }

  withRollupItems(itemMap) {
    this.put(CheckListItem.Fields.RollupItemMap, itemMap);
    return this;
  }

  // The nested questions in index order.
  rollupItemList() {
    return this.rollupItems().sortByIndex();
  }

  hasRollupItems() {
    return this.rollupItems().size() > 0;
  }

  // The nested question with this id, or a fresh item (with its own uuid) when the
  // Rollup holds no such question - test with hasRollupItem().
  rollupItem(itemId) {
    return this.rollupItems().getChecklistItem(itemId);
  }

  hasRollupItem(itemId) {
    return this.rollupItems().has(itemId);
  }

  // The Rollup's row answers, told apart by category and never by name (the operator can
  // rename them) or by index alone. Each is a fresh option when the row has none.
  rollupGoodOption() {
    var found = this.options().sortByIndex().find(option => !option.isNa() && option.isCategoryGood());
    return found || new CheckListItemOption(this.domain);
  }

  rollupReportOption() {
    var found = this.options().sortByIndex().find(option => !option.isNa() && !option.isCategoryGood());
    return found || new CheckListItemOption(this.domain);
  }

  copy() {
    var data = this._copy();
    return new CheckListItem(this.domain, data);
  }
}